/**
 * useSelectedRelatedTasks Hook
 *
 * Provides display data (title / location / due date) for the currently
 * selected related tasks, independent of the picker's paginated/filtered list
 * and of the global tasks store.
 *
 * Hydration strategy (in order of preference, so refetching is avoided):
 *  1. Tasks cached from the picker via `cacheTasks` (the picker hands over its
 *     loaded `TaskListItem[]`).
 *  2. Lightweight summaries carried by the edited task (`relatedTaskSummaries`).
 *  3. Fallback `tasksService.getById` for any id still missing from the cache.
 *
 * Mirrors the stale-guard + cache approach of `useTaskSearch`.
 *
 * @module components/tasks/wizard/steps/Step4RelatedTasks/hooks
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { logger } from '@/utils/logger';
import { tasksService } from '@/services/api/tasksService';
import { mapTaskResponseToListItem } from '@/store/mappers/taskMapper';
import { Priority } from '@/types';
import type { TaskListItem, RelatedTaskSummary } from '@/types';

interface UseSelectedRelatedTasksParams {
  /** Ids of the currently selected related tasks (form value). */
  relatedTaskIds: string[];
  /** Summaries carried by the edited task — seeds the cache to avoid fetches. */
  relatedTaskSummaries?: RelatedTaskSummary[];
}

interface UseSelectedRelatedTasksReturn {
  /** Selected tasks in `relatedTaskIds` order (only resolved ones). */
  selectedTasks: TaskListItem[];
  /** Seed the cache from the picker's loaded items (no refetch needed). */
  cacheTasks: (items: TaskListItem[]) => void;
}

/**
 * Builds a partial display item from a lightweight related-task summary.
 * Fields the summary cannot provide are zero/neutral defaults — the selected
 * list only renders title / location / due date.
 */
function summaryToListItem(summary: RelatedTaskSummary): TaskListItem {
  return {
    id: summary.id,
    shortId: summary.shortId,
    title: summary.title,
    status: summary.status,
    priority: Priority.Medium,
    assignedToId: null,
    assignedToName: null,
    assignedToAvatarUrl: null,
    assignedToIsFormerMember: false,
    startDate: null,
    dueDate: summary.dueDate,
    completedAt: null,
    location: summary.location ?? undefined,
    anchorPoint: { lat: 0, lng: 0 },
    requirementCount: 0,
    completedRequirementCount: 0,
    attachmentCount: 0,
    commentCount: 0,
    updatedAt: null,
  };
}

export function useSelectedRelatedTasks({
  relatedTaskIds,
  relatedTaskSummaries,
}: UseSelectedRelatedTasksParams): UseSelectedRelatedTasksReturn {
  // Stable cache of resolved display items keyed by task id.
  const cacheRef = useRef<Map<string, TaskListItem>>(new Map());
  // Bump to re-derive `selectedTasks` after the cache mutates.
  const [cacheVersion, setCacheVersion] = useState(0);
  // Ids already requested via getById — avoids duplicate in-flight fetches.
  const requestedIdsRef = useRef<Set<string>>(new Set());

  const cacheTasks = useCallback((items: TaskListItem[]) => {
    if (items.length === 0) return;
    let changed = false;
    items.forEach((item) => {
      if (!cacheRef.current.has(item.id)) changed = true;
      cacheRef.current.set(item.id, item);
    });
    if (changed) setCacheVersion((version) => version + 1);
  }, []);

  // Seed the cache from the edited task's summaries (only when absent).
  useEffect(() => {
    if (!relatedTaskSummaries || relatedTaskSummaries.length === 0) return;
    let changed = false;
    relatedTaskSummaries.forEach((summary) => {
      if (!cacheRef.current.has(summary.id)) {
        cacheRef.current.set(summary.id, summaryToListItem(summary));
        changed = true;
      }
    });
    if (changed) setCacheVersion((version) => version + 1);
  }, [relatedTaskSummaries]);

  // Fallback: fetch any selected id still missing from the cache.
  useEffect(() => {
    const missingIds = relatedTaskIds.filter(
      (id) => !cacheRef.current.has(id) && !requestedIdsRef.current.has(id)
    );
    if (missingIds.length === 0) return;

    missingIds.forEach((id) => requestedIdsRef.current.add(id));

    let isActive = true;
    const fetchMissing = async () => {
      try {
        const responses = await Promise.all(
          missingIds.map((id) => tasksService.getById(id))
        );
        if (!isActive) return;
        responses.forEach((response) => {
          cacheRef.current.set(
            response.data.id,
            mapTaskResponseToListItem(response.data)
          );
        });
        setCacheVersion((version) => version + 1);
      } catch (fetchError: unknown) {
        logger.error(
          'Failed to hydrate selected related tasks',
          'useSelectedRelatedTasks',
          { error: fetchError }
        );
        // Allow a retry on a later id-set change.
        missingIds.forEach((id) => requestedIdsRef.current.delete(id));
      }
    };

    fetchMissing();
    return () => {
      isActive = false;
    };
  }, [relatedTaskIds]);

  const selectedTasks = useMemo(() => {
    // `cacheVersion` is read to re-derive when the cache mutates.
    void cacheVersion;
    return relatedTaskIds
      .map((id) => cacheRef.current.get(id))
      .filter((task): task is TaskListItem => task !== undefined);
  }, [relatedTaskIds, cacheVersion]);

  return { selectedTasks, cacheTasks };
}
